import { createContext, useState, useEffect, useMemo } from 'react';
import { flushSync } from 'react-dom';
import PropTypes from 'prop-types';
import defaultTheme from '../themes/templates/default.json';
import { authService } from '../services/authService';
import { normalizeThemeId } from '../constants/ThemeType';
import { loadTemplate as loadTheme } from '../utils/templateLoader';
import usersService from '../services/usersService';
import themesService from '../services/themesService';
import { resolveThemeTokens, normalizeColorMode, applyColorModeAttribute, applyThemeCssVars } from '../theme/resolveThemeTokens';
import { runColorModeTransition } from '../theme/colorModeTransition';

// Ativa logs verbosos de tema apenas em DEV e com flag explícita.
// Para habilitar: VITE_THEME_DEBUG=true no .env.local + npm run dev
const THEME_DEBUG = import.meta.env.DEV && import.meta.env.VITE_THEME_DEBUG === 'true';

// Context para o tema
export const ThemeCustomContext = createContext();

// Quanto o boot espera pelo tema do banco antes de seguir com o JWT. Curto de propósito: API lenta
// não pode segurar a tela de "Carregando tema" (dependência lenta é pior que dependência ausente).
const DB_THEME_TIMEOUT_MS = 2500;

/**
 * Tema gravado no banco (D1: a autoridade). Devolve o theme-id, `null` quando a conta não tem tema
 * (D5 → Padrão) ou `undefined` quando não deu para saber (rede, erro, timeout) — aí quem chama cai
 * no JWT.
 */
async function fetchThemeFromDb() {
  let timer;
  try {
    const timeout = new Promise((resolve) => { timer = setTimeout(() => resolve(undefined), DB_THEME_TIMEOUT_MS); });
    const result = await Promise.race([usersService.getTheme().then((data) => data?.theme ?? null), timeout]);
    return result;
  } catch (error) {
    console.warn('[THEME] Não foi possível ler o tema do banco; seguindo com o JWT.', error);
    return undefined;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Mescla a seção `admin` do tema com a do Padrão.
 *
 * Estilo do painel = só `defaultMode`, `fontHeading` e `accent` (por modo). Fundo, texto, estados e
 * forma NÃO vêm do tema — são da escala operacional (theme/operationalModes.js). O acento não é
 * mesclado aqui: `resolveThemeTokens` resolve chave a chave, sem deixar a matiz do Padrão vazar.
 * @param {Object} customTheme seção `admin` do {id}.json
 * @returns {Object} Tema mesclado
 */
function mergeWithDefault(customTheme) {
  return {
    id: customTheme.id || defaultTheme.id,
    name: customTheme.name || defaultTheme.name,
    description: customTheme.description || defaultTheme.description,
    defaultMode: normalizeColorMode(customTheme.defaultMode || defaultTheme.defaultMode),
    fontHeading: customTheme.fontHeading || defaultTheme.fontHeading,
    accent: customTheme.accent || defaultTheme.accent,
    icons: { ...defaultTheme.icons, ...(customTheme.icons || {}) },
    labels: { ...defaultTheme.labels, ...(customTheme.labels || {}) },
    messages: { ...defaultTheme.messages, ...(customTheme.messages || {}) },
    serviceCategories: customTheme.serviceCategories || defaultTheme.serviceCategories,
  };
}

/**
 * Provider do contexto de temas customizados
 * Gerencia o tema ativo, o modo claro/escuro e fornece funções para mudança
 */
export function ThemeCustomProvider({ children }) {
  const [themeDefinition, setThemeDefinition] = useState(() => mergeWithDefault(defaultTheme));
  const [themeId, setThemeId] = useState('default');
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);
  // Escolha EXPLÍCITA do usuário (botão da barra superior). null = segue o `defaultMode` do tema.
  const [colorModePreference, setColorModePreference] = useState(null);

  // Lista de temas ativos (D10, onda 3) — vem da tabela `theme` via GET /api/themes;
  // desativar um tema ali some do seletor sem deploy. Falha de rede: seletor fica vazio
  // até recarregar (D13) — não há mais lista hardcoded para cair.
  const [availableThemes, setAvailableThemes] = useState([]);

  useEffect(() => {
    themesService.getActiveThemes()
      .then((themes) => setAvailableThemes(themes.map((t) => ({ id: t.id, name: t.displayName }))))
      .catch((error) => console.warn('[THEME] Falha ao buscar temas ativos', error));
  }, []);

  const colorMode = colorModePreference ?? themeDefinition.defaultMode;
  const themeTokens = useMemo(() => resolveThemeTokens(themeDefinition, colorMode), [themeDefinition, colorMode]);
  const currentTheme = useMemo(
    () => ({ ...themeDefinition, colors: themeTokens.colors }),
    [themeDefinition, themeTokens]
  );

  /**
   * Chaves do localStorage namespaced pelo userId
   */
  const getStorageKey = () => {
    const userInfo = authService.getUserInfo();
    return userInfo?.userId ? `agendario_theme_${userInfo.userId}` : 'agendario_theme';
  };
  const getColorModeStorageKey = () => {
    const userInfo = authService.getUserInfo();
    return userInfo?.userId ? `agendario_color_mode_${userInfo.userId}` : 'agendario_color_mode';
  };

  /**
   * Aplica um tema SEM persistir (usado no boot, onde a persistência
   * é decidida por quem chama). Normaliza, carrega e mescla com o default.
   * As CSS vars são emitidas pelo ThemeApplier.
   */
  const loadAndApply = async (rawThemeId) => {
    // Normaliza ids legados (ex.: 'COMPANY' salvo no localStorage por builds antigos —
    // o import dinâmico do JSON é case-sensitive e caía silenciosamente no default).
    const newThemeId = normalizeThemeId(rawThemeId);
    const loadedTheme = await loadTheme(newThemeId);
    setThemeDefinition(mergeWithDefault(loadedTheme));
    setThemeId(newThemeId);
    return newThemeId;
  };

  /**
   * Ação do usuário: troca de tema. Aplica na tela, grava no localStorage
   * (cache) e persiste no banco (fonte da verdade, D1).
   */
  const changeTheme = async (rawThemeId) => {
    setLoading(true);
    try {
      const newThemeId = await loadAndApply(rawThemeId);

      // Salva preferência no localStorage namespaced pelo userId
      localStorage.setItem(getStorageKey(), newThemeId);

      // Persiste no banco. Best-effort: a troca visual já aconteceu; falha
      // aqui não derruba a UI.
      try {
        await usersService.updateTheme(newThemeId);
      } catch (error) {
        console.error('[THEME] Erro ao persistir tema no backend:', error);
      }
    } catch (error) {
      console.error('Erro ao carregar tema:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Ação do usuário: fixa o modo claro/escuro. Vale para esta conta neste navegador e passa a
   * valer sobre o `defaultMode` do tema (inclusive se o tema for trocado depois).
   */
  const setColorMode = (mode) => {
    const next = normalizeColorMode(mode);
    if (next === colorMode) {
      setColorModePreference(next);
    } else {
      // A troca acontece inteira dentro do commit, de forma síncrona: atributo e vars no <html>
      // (o ThemeApplier só reaplicaria depois do paint) + render do React com flushSync. É o que
      // permite ao navegador fotografar o depois e animar a revelação (colorModeTransition.js).
      runColorModeTransition(() => {
        applyColorModeAttribute(next);
        applyThemeCssVars(resolveThemeTokens(themeDefinition, next).cssVars);
        flushSync(() => setColorModePreference(next));
      });
    }
    try {
      localStorage.setItem(getColorModeStorageKey(), next);
    } catch (error) {
      console.warn('[THEME] Não foi possível salvar o modo claro/escuro', error);
    }
  };

  const toggleColorMode = () => setColorMode(colorMode === 'dark' ? 'light' : 'dark');

  /**
   * Obtém um valor do tema com fallback para o padrão
   */
  const getThemeValue = (path) => {
    const keys = path.split('.');
    let value = currentTheme;
    let defaultValue = defaultTheme;

    for (const key of keys) {
      value = value?.[key];
      defaultValue = defaultValue?.[key];

      if (value === undefined) {
        return defaultValue;
      }
    }

    return value !== undefined ? value : defaultValue;
  };

  /**
   * Obtém um ícone do tema
   */
  const getIcon = (iconKey) => {
    const result = currentTheme.icons?.[iconKey] || defaultTheme.icons?.[iconKey] || { name: 'HelpOutline' };
    if (THEME_DEBUG) {
      console.debug(`[ThemeCustomContext.getIcon] "${iconKey}" →`, {
        found: !!currentTheme.icons?.[iconKey],
        result,
        currentThemeId: themeId,
      });
    }
    return result;
  };

  /**
   * Obtém um label do tema
   */
  const getLabel = (labelKey) => {
    const result = currentTheme.labels?.[labelKey] || defaultTheme.labels?.[labelKey] || labelKey;
    if (THEME_DEBUG) {
      console.debug(`[ThemeCustomContext.getLabel] "${labelKey}" →`, {
        found: !!currentTheme.labels?.[labelKey],
        result,
        currentThemeId: themeId,
      });
    }
    return result;
  };

  /**
   * Obtém uma mensagem do tema
   */
  const getMessage = (messageKey) => {
    const result = currentTheme.messages?.[messageKey] || defaultTheme.messages?.[messageKey] || messageKey;
    if (THEME_DEBUG) {
      console.debug(`[ThemeCustomContext.getMessage] "${messageKey}" →`, { result });
    }
    return result;
  };

  /**
   * Obtém uma cor efetiva (escala do modo + acento do tema). Chaves: primary, primaryDark,
   * background, backgroundPaper, backgroundDrawer, text, textSecondary, divider, success…
   */
  const getColor = (colorKey) => {
    const result = currentTheme.colors?.[colorKey] || '#000000';
    if (THEME_DEBUG) {
      console.debug(`[ThemeCustomContext.getColor] "${colorKey}" →`, { result, colorMode });
    }
    return result;
  };

  // Carrega tema e modo ao montar
  useEffect(() => {
    const initializeTheme = async () => {
      try {
        // 0. IMPORTANTE: Processar token do query string ANTES de tudo
        authService.processLoginCallback();

        // Modo escolhido pelo usuário (se houver) já marca o <html> antes do fetch do tema —
        // evita piscar claro numa conta que opera no escuro.
        const savedMode = localStorage.getItem(getColorModeStorageKey());
        if (savedMode === 'light' || savedMode === 'dark') {
          setColorModePreference(savedMode);
          applyColorModeAttribute(savedMode);
        }

        // 1. O BANCO é a autoridade (D1). A claim `theme` do JWT é congelada no login e dura 24h:
        //    depois de uma troca de tema ela fica velha, e usá-la aqui fazia o F5 voltar ao tema
        //    anterior (bug de 14/09 — PUT /users/theme gravava, o boot reaplicava o JWT antigo).
        const themeFromDb = await fetchThemeFromDb();

        if (themeFromDb) {
          await loadAndApply(themeFromDb);
          // Reconcilia o cache local: sobrescreve qualquer valor divergente.
          localStorage.setItem(getStorageKey(), themeFromDb);
          setInitializing(false);
          return;
        }

        // 1b. Banco indisponível (rede/timeout): o JWT é o melhor palpite — pode estar velho, mas é
        //     do servidor. Não reconcilia o cache com ele, para não gravar um valor possivelmente velho.
        const themeFromJwt = themeFromDb === undefined ? authService.getThemeFromToken() : null;

        if (themeFromJwt) {
          console.warn('[THEME] Tema do banco indisponível. Usando o JWT (pode estar desatualizado):', themeFromJwt);
          await loadAndApply(themeFromJwt);
          setInitializing(false);
          return;
        }

        // 2. Sem tema no banco (D5 → Default). localStorage só como conveniência.
        const savedThemeId = localStorage.getItem(getStorageKey());

        if (savedThemeId && savedThemeId !== 'default') {
          console.log('[THEME] Sem tema no JWT. Usando localStorage (agendario_theme):', savedThemeId);
          await loadAndApply(savedThemeId);
          setInitializing(false);
          return;
        }

        // 3. Nenhum tema encontrado - usar padrão
        console.log('[THEME] Nenhum tema encontrado. Usando tema padrão.');
        setInitializing(false);
      } catch (error) {
        console.error('[THEME] Erro ao inicializar tema:', error);
        setInitializing(false);
      }
    };

    initializeTheme();
  }, []);

  const value = {
    currentTheme,
    themeTokens,
    themeId,
    loading,
    initializing,
    availableThemes,
    colorMode,
    changeTheme,
    setColorMode,
    toggleColorMode,
    getThemeValue,
    getIcon,
    getLabel,
    getMessage,
    getColor,
  };

  // Exibe loading durante inicialização do tema (cores da escala: respeita o modo já marcado no <html>)
  if (initializing) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'var(--bg-page)',
          zIndex: 9999,
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <div
            style={{
              width: '48px',
              height: '48px',
              border: '4px solid var(--divider)',
              borderTop: '4px solid var(--color-primary)',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 16px',
            }}
          />
          <style>
            {`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}
          </style>
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px', margin: 0 }}>
            Carregando tema...
          </p>
        </div>
      </div>
    );
  }

  // Sistema carregado com tema definido
  return (
    <ThemeCustomContext.Provider value={value}>
      {children}
    </ThemeCustomContext.Provider>
  );
}

ThemeCustomProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
